import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "./helpers/base-query";
import { posApiInstance } from "./axios/api-instances";
import { Money, PaymentType } from "./types";

const baseQuery = axiosBaseQuery(posApiInstance);

export const posApi = createApi({
  reducerPath: "posApi",
  baseQuery,
  endpoints: (builder) => ({
    // DEPRECATED
    createCheckout: builder.mutation<CreateCheckoutResponse, CreateCheckoutVariables>({
      query: (data) => ({
        url: `/sku_in_box/create_checkout`,
        method: "POST",
        data,
      }),
    }),

    // DEPRECATED
    createEmptyCheckout: builder.mutation<CreateCheckoutResponse, { meta_id: string }>({
      query: (data) => ({
        url: `/sku_in_box/checkout/${data.meta_id}`,
        method: "POST",
      }),
    }),

    payCheckout: builder.mutation<PayCheckoutResponse, PayCheckoutVariables>({
      query: (data) => ({
        url: `/sku_in_box/pay_checkout`,
        method: "POST",
        data,
      }),
    }),

    checkoutStatus: builder.query<CheckoutStatusResponse, { checkoutId: string }>({
      query: ({ checkoutId }) => ({
        url: `/sku_in_box/checkout/${checkoutId}/status`,
      }),
    }),

    zones: builder.query<
      {
        results: {
          id: string;
          zone_type: number;
          description: string;
          symmetric_key: string;
          zone_min_x: number;
          zone_min_y: number;
          zone_max_x: number;
          zone_max_y: number;
        }[];
        count: 0;
      },
      void
    >({
      query: () => ({
        url: `/sku_in_box/zones`,
      }),
    }),

    zoneCart: builder.query<ZoneCartResponse, ZoneCartVariables>({
      query: ({ zoneId, ...params }) => ({
        url: `/internal/zone/${zoneId}/cart`,
        params,
      }),
    }),

    makePayment: builder.mutation<MakePaymentResponse, MakePaymentVariables>({
      query: ({ zoneId, ...data }) => ({
        url: `/internal/zone/${zoneId}/make_payment`,
        method: "POST",
        data,
      }),
    }),

    skuInBoxPaymentStatus: builder.query<PaymentStatusResponse, PaymentStatusVariables>({
      query: ({ checkoutId }) => ({
        url: `/sku_in_box/checkout/${checkoutId}/status`,
      }),
    }),
    autonomousPaymentStatus: builder.query<PaymentStatusResponse, PaymentStatusVariables>({
      query: ({ checkoutId }) => ({
        url: `/internal/checkout/${checkoutId}/status`,
      }),
    }),
    detailedVariants: builder.query<DetailedVariantsResponse, DetailedVariantsVariables>({
      query: (params) => ({
        url: `/internal/detailed_variants`,
        params,
      }),
    }),

    mobileCheckout: builder.mutation<void, MobileCheckoutVariables>({
      query: (body) => ({
        url: `/kiosk/full_autonomous/checkout`,
        method: "POST",
        data: body,
      }),
    }),
  }),
});

export const {
  usePayCheckoutMutation,
  useCreateCheckoutMutation,
  useLazyCheckoutStatusQuery,
  useCreateEmptyCheckoutMutation,
  useZonesQuery,
  useZoneCartQuery,
  useMakePaymentMutation,
  useLazyDetailedVariantsQuery,
  useMobileCheckoutMutation,
} = posApi;

export type CreateCheckoutVariables = {
  lines: {
    variant_id: string;
    quantity: number;
  }[];
};

export type CreateCheckoutResponse = {
  result: {
    id: string;
    track_id: string;
    lines: {
      id: string;
      variant: {
        id: string;
        name: string;
        sku: string;
        image: string;
        price_with_tax: Money;
        price_before_tax: Money;
        tax_price: Money;
      };
      quantity: number;
      price_with_tax: Money;
      price_before_tax: Money;
      tax_price: Money;
    }[];
    price_with_tax: Money;
    price_before_tax: Money;
    tax_price: Money;
    created_at: string;
    updated_at: string;
    discounts: {
      name: string;
      amount: Money;
    }[];
  };
};

export type PayCheckoutResponse = {
  result: {
    id: string;
    type: string;
    status_code: string;
    pos_status_code: string;
    message: string;
    order_id: string;
  };
};

export type PayCheckoutVariables = {
  checkout_id: string;
  payment_type: PaymentType;
  zone_id: string;
  checkin?: {
    timestamp: string;
    token: string;
    user_id: string;
    card_id: string;
    user_type: string;
    email: string;
  };
};

export type CheckoutStatusResponse = PayCheckoutResponse;

export type ZoneCartResponse = {
  result: {
    cart: {
      lines: {
        variant: {
          id: string;
          name: string;
          sku: string;
          image_url: string;
          price_with_tax: Money;
          price_before_tax: Money;
          tax_price: Money;
        };
        quantity: number;
        price_with_tax: Money;
        price_before_tax: Money;
        tax_price: Money;
      }[];
      price_with_tax: Money;
      price_before_tax: Money;
      tax_price: Money;
    } | null;
    track_carts: {
      track_id: number;
      lines: {
        variant: {
          id: string;
          name: string;
          sku: string;
          image_url: string;
          price_with_tax: Money;
          price_before_tax: Money;
          tax_price: Money;
        };
        quantity: number;
      }[];
      track_creation_datetime: string;
      checkout_end_datetime: string;
      is_completed: boolean;
    }[];
    track_finding_wait_duration: number;
    checkout_end_retrival_wait_duration: number;
    checkout_current_processing_datetime: string;
    total_duration_left: number;
    progress_percentage: number;
  };
};

export type ZoneCartVariables = {
  zoneId: string;
  checkout_datetime: string;
};

export type MakePaymentResponse = {
  result: string;
};

export type MakePaymentVariables = {
  zoneId: string;
  checkout_datetime: string;
  manual_lines:
    | {
        variant_id: string;
        quantity: number;
      }[]
    | null;
} & Pick<ZoneCartResponse["result"], "cart" | "track_carts">;

export type PaymentStatusResponse = {
  result: {
    id: string;
    type: string;
    status_code: string;
    pos_status_code: string;
    message: string;
    order_id: string;
  };
};

export type PaymentStatusVariables = { checkoutId: string };

export type DetailedVariantsResponse = {
  results: {
    id: string;
    name: string;
    sku: string;
    image_url: string;
    price_with_tax: Money;
    price_before_tax: Money;
    tax_price: Money;
  }[];
  count: number;
};
export type DetailedVariantsVariables = {
  sku: string;
  page?: number;
  page_size?: number;
};

type MobileCheckoutVariables = {
  timestamp: string;
  token: string;
  user_id: string;
  card_id: string;
  user_type: string;
  email: string;
  zone_id: string;
};
